// #3851 — a UX artefact was saved, but its structured data (the JSON) was rebuilt by BluePrint
// from an upstream artefact, not taken from the agent's reply. Before #3851 this was silent and a
// reviewer could approve structure the agent never wrote. AVR ruling (3 Nov 2026): save, but say so.

export type StructureRebuiltArtefactType = 'screen_spec' | 'component_catalogue' | 'design_tokens';
export type StructureRebuiltFrom = 'screen_inventory' | 'screen_specs' | 'brand_config';

export interface StructureRebuiltInfo {
  artefactType: StructureRebuiltArtefactType;
  rebuiltFrom: StructureRebuiltFrom;
}

const TYPES: readonly string[] = ['screen_spec', 'component_catalogue', 'design_tokens'];
const SOURCES: readonly string[] = ['screen_inventory', 'screen_specs', 'brand_config'];

export const STRUCTURE_REBUILT_ARTEFACT_LABEL: Record<StructureRebuiltArtefactType, string> = {
  screen_spec: 'Screen Specifications',
  component_catalogue: 'Component Catalogue',
  design_tokens: 'Design Tokens',
};

export const STRUCTURE_REBUILT_SOURCE_LABEL: Record<StructureRebuiltFrom, string> = {
  screen_inventory: 'Screen Inventory',
  screen_specs: 'Screen Specifications',
  brand_config: 'Phase 2.5 brand settings (or their defaults)',
};

export function parseStructureRebuiltEvent(data: { type: string; artefactType?: unknown; rebuiltFrom?: unknown }): StructureRebuiltInfo | null {
  if (data.type !== 'artefact_structure_rebuilt') return null;
  if (typeof data.artefactType !== 'string' || !TYPES.includes(data.artefactType)) return null;
  if (typeof data.rebuiltFrom !== 'string' || !SOURCES.includes(data.rebuiltFrom)) return null;
  return { artefactType: data.artefactType as StructureRebuiltArtefactType, rebuiltFrom: data.rebuiltFrom as StructureRebuiltFrom };
}

/** The source label for a version's confidence_metadata.structureRebuiltFrom, or null when it isn't a known source. */
export function structureRebuiltSourceLabel(from: unknown): string | null {
  return typeof from === 'string' && SOURCES.includes(from) ? STRUCTURE_REBUILT_SOURCE_LABEL[from as StructureRebuiltFrom] : null;
}

export function structureRebuiltText(info: StructureRebuiltInfo): string {
  return `Saved, but the agent's reply had no usable JSON block, so the structured data of the ${STRUCTURE_REBUILT_ARTEFACT_LABEL[info.artefactType]} was rebuilt from the ${STRUCTURE_REBUILT_SOURCE_LABEL[info.rebuiltFrom]}. Check it on the artefact page before approving.`;
}

export function StructureRebuiltNotice({ info, onDismiss }: { info: StructureRebuiltInfo; onDismiss: () => void }) {
  const label = STRUCTURE_REBUILT_ARTEFACT_LABEL[info.artefactType];
  return (
    <div
      role="status"
      data-testid={`artefact-structure-rebuilt-${info.artefactType}`}
      className="mx-6 mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex-shrink-0"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold">{label}: saved with rebuilt structure.</p>
        <button type="button" onClick={onDismiss} aria-label={`Dismiss the ${label} rebuilt-structure notice`} className="text-amber-800 hover:opacity-70">
          ×
        </button>
      </div>
      <p className="mt-1">{structureRebuiltText(info)}</p>
    </div>
  );
}
